import { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { X, Search, Image, LayoutGrid, Loader2, ChevronDown, List, Grid3X3 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { curationsService } from '../../lib/curations'

interface SelectableItem {
  type: 'contribution' | 'curation'
  id: string
  title: string
  brand?: string
  imageUrl: string | null
  slug?: string
  ownerUsername?: string
}

interface AttachmentSelectorProps {
  userId: string
  onSelect: (item: SelectableItem) => void
  onClose: () => void
}

interface CategoryOption {
  id: string
  name: string
}

type Tab = 'my-items' | 'saved' | 'curations'
type ViewMode = 'list' | 'grid'

const PAGE_SIZE = 50

export function AttachmentSelector({ userId, onSelect, onClose }: AttachmentSelectorProps) {
  const [activeTab, setActiveTab] = useState<Tab>('my-items')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [items, setItems] = useState<SelectableItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(0)

  // Category/subcategory filters
  const [categories, setCategories] = useState<CategoryOption[]>([])
  const [subcategories, setSubcategories] = useState<CategoryOption[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null)
  const [showCategoryFilter, setShowCategoryFilter] = useState(false)

  // For curations tab (small dataset, loaded once)
  const [curations, setCurations] = useState<SelectableItem[]>([])
  const [curationsLoaded, setCurationsLoaded] = useState(false)

  // Refs
  const sentinelRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(null)
  const savedCurationIdRef = useRef<string | null>(null)

  // Debounce search query
  useEffect(() => {
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current) }
  }, [searchQuery])

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      const [catRes, subRes] = await Promise.all([
        supabase.from('categories').select('id, name').order('name'),
        supabase.from('subcategories').select('id, name, category_id').order('name')
      ])
      if (catRes.data) setCategories(catRes.data)
      if (subRes.data) setSubcategories(subRes.data)
    }
    loadCategories()
  }, [])

  // Resolve saved curation ID once
  useEffect(() => {
    const loadDefaultCuration = async () => {
      const defaultCuration = await curationsService.getUserDefaultCuration(userId)
      savedCurationIdRef.current = defaultCuration?.id || null
    }
    loadDefaultCuration()
  }, [userId])

  // Filtered subcategories based on selected category
  const filteredSubcategories = selectedCategory
    ? subcategories.filter((s: any) => s.category_id === selectedCategory)
    : subcategories

  // Fetch items from DB (server-side search + filters + pagination)
  const fetchItems = useCallback(async (
    tab: Tab,
    query: string,
    categoryId: string | null,
    subcategoryId: string | null,
    pageNum: number,
    append: boolean
  ) => {
    if (tab === 'curations') return // curations handled separately

    if (pageNum === 0) {
      setIsLoading(true)
    } else {
      setIsLoadingMore(true)
    }

    try {
      let results: SelectableItem[] = []
      const offset = pageNum * PAGE_SIZE

      if (tab === 'my-items') {
        const hasCategory = !!categoryId
        const hasSubcategory = !!subcategoryId
        const classificationJoin = (hasCategory || hasSubcategory)
          ? 'item_classifications!inner'
          : 'item_classifications'

        let dbQuery = supabase
          .from('contributions')
          .select(`id, title, brand, cached_image_url, ${classificationJoin}(category_id, subcategory_id)`)
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .range(offset, offset + PAGE_SIZE - 1)

        if (query) {
          dbQuery = dbQuery.or(`title.ilike.%${query}%,brand.ilike.%${query}%`)
        }
        if (hasCategory) {
          dbQuery = dbQuery.eq('item_classifications.category_id', categoryId)
        }
        if (hasSubcategory) {
          dbQuery = dbQuery.eq('item_classifications.subcategory_id', subcategoryId)
        }

        const { data } = await dbQuery
        results = (data || []).map(c => ({
          type: 'contribution' as const,
          id: c.id,
          title: c.title || c.brand || 'Untitled Item',
          brand: c.brand,
          imageUrl: c.cached_image_url
        }))
      } else if (tab === 'saved') {
        if (!savedCurationIdRef.current) {
          // Wait briefly for curation ID to resolve
          await new Promise(r => setTimeout(r, 200))
        }
        const curationId = savedCurationIdRef.current
        if (!curationId) {
          setItems([])
          setHasMore(false)
          setIsLoading(false)
          setIsLoadingMore(false)
          return
        }

        const hasCategory = !!categoryId
        const hasSubcategory = !!subcategoryId
        const classificationJoin = (hasCategory || hasSubcategory)
          ? 'item_classifications!inner'
          : 'item_classifications'

        let dbQuery = supabase
          .from('issue_items')
          .select(`
            contribution_id,
            contributions!inner (
              id, title, brand, cached_image_url,
              ${classificationJoin}(category_id, subcategory_id)
            )
          `)
          .eq('issue_id', curationId)
          .order('created_at', { ascending: false })
          .range(offset, offset + PAGE_SIZE - 1)

        // For saved items, text search filters on the nested contribution
        if (query) {
          dbQuery = dbQuery.or(`title.ilike.%${query}%,brand.ilike.%${query}%`, { referencedTable: 'contributions' })
        }
        if (hasCategory) {
          dbQuery = dbQuery.eq('contributions.item_classifications.category_id', categoryId)
        }
        if (hasSubcategory) {
          dbQuery = dbQuery.eq('contributions.item_classifications.subcategory_id', subcategoryId)
        }

        const { data } = await dbQuery
        results = (data || []).map(row => {
          const c = (row as any).contributions
          return {
            type: 'contribution' as const,
            id: c.id,
            title: c.title || c.brand || 'Untitled Item',
            brand: c.brand,
            imageUrl: c.cached_image_url
          }
        })
      }

      setHasMore(results.length === PAGE_SIZE)
      setItems(prev => append ? [...prev, ...results] : results)
    } catch (error) {
      console.error('Failed to load items:', error)
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [userId])

  // Load curations (once, client-side search is fine for curations)
  const loadCurations = useCallback(async () => {
    if (curationsLoaded) return
    setIsLoading(true)
    try {
      const res = await curationsService.getUserCurations(userId)
      if (res) {
        setCurations(res.map(c => ({
          type: 'curation' as const,
          id: c.id,
          title: c.title || 'Untitled Curation',
          imageUrl: c.highlighted_image_url || null,
          slug: c.slug,
        })))
      }
      setCurationsLoaded(true)
    } catch (error) {
      console.error('Failed to load curations:', error)
    } finally {
      setIsLoading(false)
    }
  }, [userId, curationsLoaded])

  // Trigger fetch when tab, search, or filters change
  useEffect(() => {
    setPage(0)
    if (activeTab === 'curations') {
      loadCurations()
    } else {
      fetchItems(activeTab, debouncedQuery, selectedCategory, selectedSubcategory, 0, false)
    }
  }, [activeTab, debouncedQuery, selectedCategory, selectedSubcategory, fetchItems, loadCurations])

  // Load more (next page)
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || activeTab === 'curations') return
    const nextPage = page + 1
    setPage(nextPage)
    fetchItems(activeTab, debouncedQuery, selectedCategory, selectedSubcategory, nextPage, true)
  }, [page, isLoadingMore, hasMore, activeTab, debouncedQuery, selectedCategory, selectedSubcategory, fetchItems])

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore() },
      { root: scrollContainerRef.current, threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])

  // For curations, apply client-side search
  const displayItems = activeTab === 'curations'
    ? (debouncedQuery
        ? curations.filter(c =>
            c.title.toLowerCase().includes(debouncedQuery.toLowerCase())
          )
        : curations)
    : items

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab)
    setSearchQuery('')
    setDebouncedQuery('')
    setSelectedCategory(null)
    setSelectedSubcategory(null)
    setShowCategoryFilter(false)
  }

  const handleCategorySelect = (catId: string | null) => {
    setSelectedCategory(catId)
    setSelectedSubcategory(null) // reset subcategory when category changes
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'my-items', label: 'My Shares' },
    { key: 'saved', label: 'Saved' },
    { key: 'curations', label: 'Curations' },
  ]

  const emptyMessages: Record<Tab, string> = {
    'my-items': "You haven't shared any items yet",
    'saved': 'No saved items',
    'curations': 'No curations yet',
  }

  const showFilters = activeTab !== 'curations'
  const selectedCategoryName = categories.find(c => c.id === selectedCategory)?.name
  const selectedSubcategoryName = subcategories.find(s => s.id === selectedSubcategory)?.name

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel — mobile: bottom sheet, desktop: popover */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-x-0 bottom-0 z-50 sm:absolute sm:bottom-full sm:inset-x-auto sm:right-0 sm:mb-2 sm:w-[425px]"
      >
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-gray-200 overflow-hidden flex flex-col h-[65vh] sm:h-[420px]">
          {/* Mobile drag handle */}
          <div className="sm:hidden flex justify-center pt-2 pb-1">
            <div className="w-8 h-1 rounded-full bg-gray-300" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-1 sm:pt-3 pb-2">
            <h3 className="text-sm font-semibold text-gray-900">Share from your Curations</h3>
            <div className="flex items-center gap-1">
              {/* View toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  title="List view"
                >
                  <List className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1 rounded transition-colors ${
                    viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  title="Grid view"
                >
                  <Grid3X3 className="h-3.5 w-3.5" />
                </button>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100 px-4">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`px-3 py-2 text-xs font-medium transition-colors relative ${
                  activeTab === tab.key
                    ? 'text-[#57241F]'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <motion.div
                    layoutId="attachment-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#57241F]"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="px-3 py-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#57241F]/30 focus:border-[#57241F]"
              />
            </div>
          </div>

          {/* Category/Subcategory filters */}
          {showFilters && (
            <div className="px-3 pb-2">
              <button
                onClick={() => setShowCategoryFilter(!showCategoryFilter)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ChevronDown className={`h-3 w-3 transition-transform ${showCategoryFilter ? 'rotate-180' : ''}`} />
                {selectedCategoryName || selectedSubcategoryName ? (
                  <span className="text-[#57241F] font-medium">
                    {selectedCategoryName}{selectedSubcategoryName ? ` / ${selectedSubcategoryName}` : ''}
                  </span>
                ) : (
                  'Filter by category'
                )}
                {(selectedCategory || selectedSubcategory) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCategorySelect(null)
                    }}
                    className="ml-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </button>

              {showCategoryFilter && (
                <div className="mt-1.5 space-y-1.5">
                  {/* Category chips */}
                  <div className="flex flex-wrap gap-1">
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(selectedCategory === cat.id ? null : cat.id)}
                        className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                          selectedCategory === cat.id
                            ? 'bg-[#57241F] text-white border-[#57241F]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>

                  {/* Subcategory chips (show when category selected) */}
                  {selectedCategory && filteredSubcategories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {filteredSubcategories.map(sub => (
                        <button
                          key={sub.id}
                          onClick={() => setSelectedSubcategory(selectedSubcategory === sub.id ? null : sub.id)}
                          className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                            selectedSubcategory === sub.id
                              ? 'bg-[#57241F]/80 text-white border-[#57241F]/80'
                              : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {sub.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Items list */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto min-h-0 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
              </div>
            ) : displayItems.length === 0 ? (
              <div className="text-center py-8 px-4">
                <p className="text-sm text-gray-400">
                  {searchQuery || selectedCategory ? 'No matches found' : emptyMessages[activeTab]}
                </p>
              </div>
            ) : viewMode === 'list' ? (
              <div className="space-y-0.5">
                {displayItems.map(item => (
                  <button
                    key={`${item.type}-${item.id}`}
                    onClick={() => onSelect(item)}
                    className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl hover:bg-gray-50 transition-colors text-left"
                  >
                    {/* Thumbnail */}
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {item.type === 'contribution' ? (
                            <Image className="h-4 w-4 text-gray-400" />
                          ) : (
                            <LayoutGrid className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                      {item.brand && (
                        <p className="text-xs text-gray-500 truncate">{item.brand}</p>
                      )}
                      {item.type === 'curation' && (
                        <p className="text-xs text-gray-400">Curation</p>
                      )}
                    </div>
                  </button>
                ))}

                {/* Infinite scroll sentinel */}
                {activeTab !== 'curations' && hasMore && (
                  <div ref={sentinelRef} className="flex items-center justify-center py-3">
                    {isLoadingMore && <Loader2 className="h-4 w-4 text-gray-300 animate-spin" />}
                  </div>
                )}
              </div>
            ) : (
              /* Grid view - 3 column image grid */
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 px-0.5">
                  {displayItems.map(item => (
                    <button
                      key={`${item.type}-${item.id}`}
                      onClick={() => onSelect(item)}
                      className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-80 transition-opacity group"
                    >
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {item.type === 'contribution' ? (
                            <Image className="h-6 w-6 text-gray-300" />
                          ) : (
                            <LayoutGrid className="h-6 w-6 text-gray-300" />
                          )}
                        </div>
                      )}
                      {/* Title overlay — always visible on mobile, hover on desktop */}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <p className="text-[10px] text-white leading-tight line-clamp-2">{item.title}</p>
                      </div>
                      {/* Curation badge */}
                      {item.type === 'curation' && (
                        <div className="absolute top-1 left-1">
                          <LayoutGrid className="h-3 w-3 text-white drop-shadow-md" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Infinite scroll sentinel */}
                {activeTab !== 'curations' && hasMore && (
                  <div ref={sentinelRef} className="flex items-center justify-center py-3">
                    {isLoadingMore && <Loader2 className="h-4 w-4 text-gray-300 animate-spin" />}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </>
  )
}
